import { Drawer, Select, Typography } from 'antd';
import { useWatch } from 'antd/lib/form/Form';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import useAPIQuery from '../../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../../api/useInfiniteAPIQuery';
import Form from '../../../components/Form';
import RegistrationEnabledFieldsTags from '../../../components/RegistrationEnabledFieldsTags';
import RegistrationTemplateForm, {
  RegistrationTemplateFormValues,
} from '../../../components/RegistrationTemplateForm/RegistrationTemplateForm';
import usePrefetchPages from '../../../hooks/usePrefetchPages';
import usePrevious from '../../../hooks/usePrevious';
import useTypedParams from '../../../hooks/useTypedParams';
import { JobPostingId } from '../../../types';

interface JobPostingRegistrationTemplateFormValues
  extends Omit<RegistrationTemplateFormValues, 'name' | 'defaultTemplate'> {
  id: number;
}

interface JobPostingRegistrationTemplateSerializer {
  id: number;
  name: string;
  block_edit: boolean;
  linkedin_or_resume: 'REQUIRED' | 'NOT_REQUIRED';
  linkedin: 'REQUIRED' | 'NOT_REQUIRED';
  resume: 'REQUIRED' | 'NOT_REQUIRED';
  cpf: 'VISIBLE' | 'VISIBLE_AND_REQUIRED' | 'NOT_VISIBLE';
  portfolio: 'VISIBLE' | 'VISIBLE_AND_REQUIRED' | 'NOT_VISIBLE';
  pcd: 'VISIBLE' | 'VISIBLE_AND_REQUIRED' | 'NOT_VISIBLE';
  pcd_report: 'VISIBLE' | 'VISIBLE_AND_REQUIRED' | 'NOT_VISIBLE';
  cid: 'VISIBLE' | 'VISIBLE_AND_REQUIRED' | 'NOT_VISIBLE';
  address: 'VISIBLE' | 'VISIBLE_AND_REQUIRED' | 'NOT_VISIBLE';
  relocation: 'VISIBLE' | 'VISIBLE_AND_REQUIRED' | 'NOT_VISIBLE';
  travel: 'VISIBLE' | 'VISIBLE_AND_REQUIRED' | 'NOT_VISIBLE';
  salary: 'VISIBLE' | 'VISIBLE_AND_REQUIRED' | 'NOT_VISIBLE';
  birthday: 'VISIBLE' | 'VISIBLE_AND_REQUIRED' | 'NOT_VISIBLE';
  created_at: string;
  updated_at: string;
}

const registrationTemplateToFormValues: (
  registrationTemplate?: Omit<JobPostingRegistrationTemplateSerializer, 'block_edit'>
) => JobPostingRegistrationTemplateFormValues = (registrationTemplate) => ({
  id: registrationTemplate?.id || 0,
  linkedinOrResume: registrationTemplate?.linkedin_or_resume || 'NOT_REQUIRED',
  linkedin: registrationTemplate?.linkedin || 'NOT_REQUIRED',
  resume: registrationTemplate?.resume || 'NOT_REQUIRED',
  cpf: registrationTemplate?.cpf || 'NOT_VISIBLE',
  pcd: registrationTemplate?.pcd || 'NOT_VISIBLE',
  cid: registrationTemplate?.cid || 'NOT_VISIBLE',
  pcdReport: registrationTemplate?.pcd_report || 'NOT_VISIBLE',
  birthday: registrationTemplate?.birthday || 'NOT_VISIBLE',
  salary: registrationTemplate?.salary || 'NOT_VISIBLE',
  address: registrationTemplate?.address || 'NOT_VISIBLE',
  relocation: registrationTemplate?.relocation || 'NOT_VISIBLE',
  travel: registrationTemplate?.travel || 'NOT_VISIBLE',
  portfolio: registrationTemplate?.portfolio || 'NOT_VISIBLE',
});

const JobPostingRegistrationTemplateForm = () => {
  const { t } = useTranslation();
  const { id } = useTypedParams({ id: JobPostingId });

  const [form] = Form.useForm();

  const [editFormDrawerVisible, setEditFormDrawerVisible] = useState(false);

  const handleClose = () => {
    setEditFormDrawerVisible(false);
  };

  const config = useAPIQuery('/configs/tenant-configurations/');

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const registrationTemplatesPages = useInfiniteAPIQuery('/registration-templates/');

  const registrationTemplates = usePrefetchPages(
    registrationTemplatesPages,
    (data) => data.results
  );

  // TODO: implementar api
  const enhancedJobPostingData = {
    ...jobPosting.data,
    registrationTemplate: {
      block_edit: false,
      id: 1,
      name: 'Formulário Padrão',
      linkedin_or_resume: 'NOT_REQUIRED',
      linkedin: 'NOT_REQUIRED',
      resume: 'NOT_REQUIRED',
      cpf: 'VISIBLE_AND_REQUIRED',
      portfolio: 'NOT_VISIBLE',
      pcd: 'NOT_VISIBLE',
      pcd_report: 'NOT_VISIBLE',
      cid: 'NOT_VISIBLE',
      address: 'NOT_VISIBLE',
      relocation: 'NOT_VISIBLE',
      travel: 'NOT_VISIBLE',
      salary: 'NOT_VISIBLE',
      birthday: 'NOT_VISIBLE',
      created_at: '2026-03-11T21:33:17.688988Z',
      updated_at: '2026-03-16T14:54:08.853667Z',
    } as JobPostingRegistrationTemplateSerializer,
  };

  const registrationTemplate = enhancedJobPostingData.registrationTemplate;

  const fieldsValues = useWatch<JobPostingRegistrationTemplateFormValues | undefined>([], form);
  const prevFieldValue = usePrevious(fieldsValues);

  useEffect(() => {
    const prev = prevFieldValue;
    const next = fieldsValues;

    if (next && prev && next.id === prev.id && next.id !== 0) {
      const someFieldHasChanged = (
        Object.keys(next) as (keyof JobPostingRegistrationTemplateFormValues)[]
      )
        .filter((key) => key !== 'id')
        .some((key) => next[key] !== prev[key]);

      if (someFieldHasChanged) {
        return form.setFieldValue('id', 0);
      }
    }
  }, [fieldsValues, form, prevFieldValue]);

  return (
    <Form<JobPostingRegistrationTemplateFormValues>
      layout="vertical"
      form={form}
      name="jobPostingRegistrationTemplate"
      initialValues={registrationTemplateToFormValues(registrationTemplate)}
      onFinish={(variables) => {
        // TODO: implementar api
      }}
      onValuesChange={(changedValues) => {
        if (changedValues.id !== undefined && changedValues.id !== 0) {
          const registrationTemplate = registrationTemplates.find(
            ({ id }) => id === changedValues.id
          );

          return form.setFieldsValue(registrationTemplateToFormValues(registrationTemplate));
        }
      }}
      component={false}
    >
      <Drawer
        open={editFormDrawerVisible}
        onClose={handleClose}
        width={550}
        title={t('Editar formulário de inscrição')}
        forceRender
      >
        <RegistrationTemplateForm
          isSankhyaActive={config.data?.enableSankhyaAdmission}
          isVixtingActive={config.data?.enableVixtingAdmission}
          disableDefaultTemplateField
          disableNameField
        />
      </Drawer>
      <Form.Row>
        <Form.Col>
          <Typography.Paragraph>
            <Trans>
              Selecione um modelo de questionário para utilizar neste processo seletivo caso deseje
              modificar o modelo padrão. As mudanças aplicadas ao modelo selecionado pelo botão de
              edição abaixo serão aplicadas a esta vaga e não afetarão o uso do modelo em outros
              processos seletivos.
            </Trans>
          </Typography.Paragraph>
        </Form.Col>
      </Form.Row>
      <Form.Row>
        <Form.Col span={8}>
          <Form.Item label={t('Selecione um modelo')} name="id">
            <Select
              options={[
                { key: 0, value: 0, label: t('Personalizado') },
                ...registrationTemplates.map(({ id, name }) => ({
                  key: id,
                  value: id,
                  label: name,
                })),
              ]}
              disabled={enhancedJobPostingData.registrationTemplate.block_edit}
            />
          </Form.Item>
        </Form.Col>
      </Form.Row>
      <Form.Row>
        <Form.Col>
          <Typography.Paragraph strong>
            <Trans>Campos habilitados</Trans>
          </Typography.Paragraph>
        </Form.Col>
        <Form.Col flex={1} />
        <Form.Col>
          <Typography.Link
            onClick={() => setEditFormDrawerVisible(true)}
            disabled={enhancedJobPostingData.registrationTemplate.block_edit}
          >
            <Trans>Editar</Trans>
          </Typography.Link>
        </Form.Col>
      </Form.Row>
      <RegistrationEnabledFieldsTags
        fields={{
          address: fieldsValues?.address || 'NOT_VISIBLE',
          birthday: fieldsValues?.birthday || 'NOT_VISIBLE',
          cid: fieldsValues?.cid || 'NOT_VISIBLE',
          cpf: fieldsValues?.cpf || 'NOT_VISIBLE',
          pcd: fieldsValues?.pcd || 'NOT_VISIBLE',
          pcd_report: fieldsValues?.pcdReport || 'NOT_VISIBLE',
          portfolio: fieldsValues?.portfolio || 'NOT_VISIBLE',
          relocation: fieldsValues?.relocation || 'NOT_VISIBLE',
          salary: fieldsValues?.salary || 'NOT_VISIBLE',
          travel: fieldsValues?.travel || 'NOT_VISIBLE',
        }}
        alwaysRequiredCpf={
          config.data?.enableVixtingAdmission || config.data?.enableSankhyaAdmission
        }
      />
    </Form>
  );
};

export default JobPostingRegistrationTemplateForm;
